import type { FormEvent, ChangeEvent } from "react";

export type AuthPanelSsoProvider = {
  slug: string;
  displayName: string;
  buttonText: string;
  accentColor?: string;
  icon?: string | null;
};

export type AuthFeedback = {
  type: "info" | "error";
  message: string;
};

export interface AuthPanelProps {
  ssoProviders?: AuthPanelSsoProvider[];
  ssoLoading?: boolean;
  ssoError?: string | null;
  ssoSubmitting?: string | null;
  onSsoProviderClick?: (provider: AuthPanelSsoProvider) => void;
  title?: string;
  description?: string;
  feedback?: AuthFeedback | null;
  authSubmitting: "login" | "signup" | null;
  loginForm: {
    email: string;
    password: string;
  };
  signupForm: {
    email: string;
    password: string;
    displayName: string;
  };
  onLoginChange: (next: { email: string; password: string }) => void;
  onSignupChange: (next: { email: string; password: string; displayName: string }) => void;
  onLoginSubmit: (event: FormEvent<HTMLFormElement>) => void;
  onSignupSubmit: (event: FormEvent<HTMLFormElement>) => void;
  className?: string;
}

export function AuthPanel(props: AuthPanelProps) {
  const {
    title = "Anmeldung",
    description = "Melde dich mit deinem Konto an oder registriere dich, um den Assistenten zu nutzen.",
    feedback = null,
    authSubmitting,
    loginForm,
    signupForm,
    onLoginChange,
    onSignupChange,
    onLoginSubmit,
    onSignupSubmit,
    ssoProviders = [],
    ssoLoading = false,
    ssoError = null,
    ssoSubmitting = null,
    onSsoProviderClick,
    className
  } = props;

  const handleLoginInputChange = (event: ChangeEvent<HTMLInputElement>) => {
    const { name, value } = event.target;
    onLoginChange({
      ...loginForm,
      [name]: value
    });
  };

  const handleSignupInputChange = (event: ChangeEvent<HTMLInputElement>) => {
    const { name, value } = event.target;
    onSignupChange({
      ...signupForm,
      [name]: value
    });
  };

  const panelClassName = ["auth-panel", className].filter(Boolean).join(" ");

  const handleSsoClick = (provider: AuthPanelSsoProvider) => {
    if (authSubmitting || !onSsoProviderClick) {
      return;
    }
    onSsoProviderClick(provider);
  };

  const renderProviderIcon = (provider: AuthPanelSsoProvider) => {
    if (!provider.icon) {
      return <span className="auth-sso-icon-text">SSO</span>;
    }

    if (provider.icon === "microsoft") {
      return (
        <svg className="auth-sso-icon" viewBox="0 0 24 24" aria-hidden="true">
          <rect x="2" y="2" width="9" height="9" fill="#f35325" />
          <rect x="13" y="2" width="9" height="9" fill="#81bc06" />
          <rect x="2" y="13" width="9" height="9" fill="#05a6f0" />
          <rect x="13" y="13" width="9" height="9" fill="#ffba08" />
        </svg>
      );
    }

    return <span className="auth-sso-icon-text">{provider.icon.slice(0, 3).toUpperCase()}</span>;
  };

  return (
    <div className={panelClassName}>
      <h2>{title}</h2>
      <p className="auth-intro">{description}</p>
      {feedback ? <p className={`auth-feedback ${feedback.type}`}>{feedback.message}</p> : null}
      {ssoLoading || ssoProviders.length > 0 || ssoError ? (
        <div className="auth-sso-section">
          {ssoLoading ? <span className="auth-sso-spinner" aria-live="polite" aria-busy="true" /> : null}
          {ssoError ? <p className="auth-sso-error">{ssoError}</p> : null}
          <div className="auth-sso-buttons">
            {ssoProviders.map((provider) => {
              const isSubmitting = ssoSubmitting === provider.slug;
              const isDisabled = Boolean(authSubmitting) || Boolean(ssoSubmitting);
              const style = provider.accentColor
                ? { borderColor: provider.accentColor, color: provider.accentColor }
                : undefined;
              return (
                <button
                  key={provider.slug}
                  type="button"
                  className="auth-sso-button"
                  style={style}
                  onClick={() => handleSsoClick(provider)}
                  disabled={isDisabled}
                >
                  <span className="auth-sso-icon-wrapper" aria-hidden="true">
                    {renderProviderIcon(provider)}
                  </span>
                  <span>{isSubmitting ? "Weiterleitung…" : provider.buttonText}</span>
                </button>
              );
            })}
          </div>
        </div>
      ) : null}
    </div>
  );
}
